import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Card } from "./ui/card";
import { Badge } from "./ui/badge";
import { Shield, TrendingUp, AlertTriangle, Clock, History } from "lucide-react";

const TAG_STYLES = {
    Excellent: "bg-emerald-50 text-emerald-700 border-emerald-200",
    Good: "bg-blue-50 text-blue-800 border-blue-200",
    Average: "bg-amber-50 text-amber-800 border-amber-200",
    Risky: "bg-rose-50 text-rose-700 border-rose-200",
};

const BAR_COLORS = {
    Excellent: "bg-emerald-500",
    Good: "bg-blue-600",
    Average: "bg-amber-500",
    Risky: "bg-rose-500",
};

export default function TrustScoreCard({ customerId }) {
    const [data, setData] = useState(null);
    const [err, setErr] = useState(null);

    useEffect(() => {
        let active = true;
        api
            .get(`/customers/${customerId}/trust-score`)
            .then((r) => active && setData(r.data))
            .catch((e) => active && setErr(e?.response?.data?.detail || "Failed"));
        return () => {
            active = false;
        };
    }, [customerId]);

    if (err) return null;
    if (!data) {
        return (
            <Card
                className="rounded-2xl border-slate-100 p-5"
                data-testid="trust-score-loading"
            >
                <div className="text-sm text-slate-400">Computing trust score…</div>
            </Card>
        );
    }

    const f = data.factors;
    const barColor = BAR_COLORS[data.tag] || "bg-slate-500";

    return (
        <Card
            className="rounded-2xl border-slate-100 p-5 sm:p-6"
            data-testid="trust-score-card"
        >
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center gap-2.5">
                    <div className="h-9 w-9 rounded-xl bg-indigo-50 grid place-items-center">
                        <Shield className="h-4 w-4 text-indigo-700" />
                    </div>
                    <div>
                        <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">
                            Internal · Admin Only
                        </div>
                        <h3 className="font-display font-bold text-slate-900 text-base">
                            Customer Trust Score
                        </h3>
                    </div>
                </div>
                <Badge
                    data-testid="trust-score-tag"
                    className={`${TAG_STYLES[data.tag] || ""} px-3 py-1 text-sm font-bold border`}
                >
                    {data.tag}
                </Badge>
            </div>

            <div className="mt-4 flex items-end gap-3">
                <div
                    className="num font-display text-4xl font-bold text-slate-900"
                    data-testid="trust-score-value"
                >
                    {data.score}
                </div>
                <div className="text-sm text-slate-500 pb-1.5">/ 100</div>
            </div>
            <div className="mt-2 h-2 bg-slate-100 rounded-full overflow-hidden">
                <div
                    className={`h-full ${barColor} transition-all`}
                    style={{ width: `${data.score}%` }}
                />
            </div>

            <div className="mt-5 grid sm:grid-cols-2 gap-3">
                <Factor
                    icon={Clock}
                    label="On-time Payments"
                    weight={40}
                    value={`${Math.round(f.on_time_ratio * 100)}%`}
                    sub={`${f.on_time_count}/${f.total_payments} payments on time`}
                    testid="factor-ontime"
                />
                <Factor
                    icon={AlertTriangle}
                    label="Overdue Penalty"
                    weight={30}
                    value={
                        f.overdue_active_loans
                            ? `${f.avg_overdue_days}d avg`
                            : "No overdue"
                    }
                    sub={`${f.overdue_active_loans} active overdue loan(s)`}
                    testid="factor-overdue"
                />
                <Factor
                    icon={TrendingUp}
                    label="Repayment Progress"
                    weight={20}
                    value={`${Math.round(f.repayment_progress * 100)}%`}
                    sub={`₹${formatNum(f.total_principal_paid)} of ₹${formatNum(
                        f.total_principal_disbursed
                    )}`}
                    testid="factor-repayment"
                />
                <Factor
                    icon={History}
                    label="Loan History Depth"
                    weight={10}
                    value={`${f.closed_loans} closed`}
                    sub="Track record of completed loans"
                    testid="factor-history"
                />
            </div>

            <div className="mt-4 pt-4 border-t border-slate-100 text-[11px] text-slate-500">
                Score is computed internally and is <b>never shown</b> to the customer,
                shared in WhatsApp messages, or printed on certificates. Employees do not
                have access to this score.
            </div>
        </Card>
    );
}

function Factor({ icon: Icon, label, weight, value, sub, testid }) {
    return (
        <div
            className="rounded-xl bg-slate-50 border border-slate-100 p-3"
            data-testid={testid}
        >
            <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-2">
                    <Icon className="h-3.5 w-3.5 text-slate-600" strokeWidth={2} />
                    <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">
                        {label}
                    </div>
                </div>
                <span className="text-[10px] font-mono bg-white border border-slate-200 text-slate-600 px-1.5 py-0.5 rounded">
                    {weight}%
                </span>
            </div>
            <div className="num font-bold text-slate-900 text-base mt-1">{value}</div>
            <div className="text-[11px] text-slate-500 mt-0.5">{sub}</div>
        </div>
    );
}

function formatNum(n) {
    return new Intl.NumberFormat("en-IN").format(Math.round(n || 0));
}
